/**
 * SetPassword page — first-login forced password reset.
 *
 * Trigger: `user.must_change_password === true` after login.
 * Route:   /set-password  (rendered inside ProtectedRoute so the JWT
 *          is guaranteed valid before we allow submission).
 *
 * On success we swap in a fresh token + user (backend clears the flag
 * and hands us a new JWT) and drop the LO on the dashboard.
 */
import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, ShieldCheck, ArrowRight } from 'lucide-react';
import { setInitialPassword, useAuth, logout as doLogout } from '../lib/authStore';

// Champagne palette — kept identical to the Login screen so the two
// pages feel like a single continuous moment.
const GOLD = '#e8cc8c';
const GOLD_DEEP = '#b89867';

export default function SetPassword() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [pw, setPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pwRef = useRef(null);
  useEffect(() => { pwRef.current?.focus(); }, []);

  // Strength gate mirrors the backend `Field(min_length=8)`.
  const rules = {
    len: pw.length >= 8,
    upper: /[A-Z]/.test(pw),
    number: /[0-9]/.test(pw),
    match: pw.length > 0 && pw === confirm,
  };
  const canSubmit = rules.len && rules.upper && rules.number && rules.match && !loading;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!canSubmit) return;
    setLoading(true);
    try {
      await setInitialPassword(pw);
      toast.success('Password set — welcome in.');
      nav('/', { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Could not set your password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const bailOut = async () => {
    await doLogout();
    nav('/login', { replace: true });
  };

  const firstName = (user?.name || '').split(' ')[0] || 'there';

  return (
    <div
      data-testid="set-password-page"
      className="relative min-h-screen overflow-hidden flex items-center justify-center px-4 py-10"
      style={{
        background:
          'radial-gradient(ellipse 80% 60% at 50% 0%, #0c0c0d 0%, #060606 60%, #030303 100%)',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Inter", "Helvetica Neue", system-ui, sans-serif',
      }}
    >
      {/* Warm floor wash — same trick used on the Login screen so the
          card sits over a subtle champagne horizon. */}
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 h-[36%]"
        style={{
          background:
            'linear-gradient(to top, rgba(232,204,140,0.06) 0%, rgba(232,204,140,0.02) 30%, transparent 80%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      <div className="relative z-10 w-full max-w-[440px] flex flex-col items-center">
        {/* Brand wordmark */}
        <div
          aria-label="Kinfolk"
          className="select-none"
          style={{
            width: '260px',
            height: '52px',
            backgroundColor: GOLD,
            WebkitMaskImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 200'%3E%3Ctext x='400' y='140' text-anchor='middle' font-family='Cormorant Garamond, serif' font-size='120' font-weight='500' letter-spacing='16'%3EKINFOLK%3C/text%3E%3C/svg%3E\")",
            maskImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 800 200'%3E%3Ctext x='400' y='140' text-anchor='middle' font-family='Cormorant Garamond, serif' font-size='120' font-weight='500' letter-spacing='16'%3EKINFOLK%3C/text%3E%3C/svg%3E\")",
            WebkitMaskRepeat: 'no-repeat',
            maskRepeat: 'no-repeat',
            WebkitMaskSize: 'contain',
            maskSize: 'contain',
          }}
        />
        {/* Gold divider */}
        <div className="mt-3 h-px w-24" style={{ backgroundColor: GOLD_DEEP }} />

        <div className="mt-6 text-center">
          <div
            className="text-[10.5px] uppercase tracking-[0.32em] font-bold"
            style={{ color: GOLD }}
          >
            Welcome, {firstName}
          </div>
          <h1 className="mt-3 text-[26px] leading-[1.15] tracking-tight text-neutral-100 font-semibold">
            Set your permanent password
          </h1>
          <p className="mt-2 text-[13px] text-neutral-400 max-w-sm mx-auto leading-relaxed">
            Your temp password will stop working after this step. Pick something you'll remember —
            we'll never make you rotate it on a schedule.
          </p>
        </div>

        <form onSubmit={submit} className="w-full mt-8 space-y-3">
          {/* New password */}
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-neutral-500">
              New password
            </span>
            <div className="relative mt-1.5">
              <input
                ref={pwRef}
                data-testid="new-password-input"
                type={showPw ? 'text' : 'password'}
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                autoComplete="new-password"
                className="w-full h-12 px-4 pr-11 rounded-lg bg-neutral-900/70 border border-neutral-800 text-neutral-100 text-[14px] placeholder-neutral-600 focus:outline-none focus:border-[hsl(var(--kf-gold))]/70 transition"
                placeholder="At least 8 characters"
              />
              <button
                type="button"
                onClick={() => setShowPw((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md text-neutral-500 hover:text-neutral-300 flex items-center justify-center"
                aria-label={showPw ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </label>

          {/* Confirm */}
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.22em] font-bold text-neutral-500">
              Confirm new password
            </span>
            <div className="relative mt-1.5">
              <input
                data-testid="confirm-password-input"
                type={showConfirm ? 'text' : 'password'}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                className="w-full h-12 px-4 pr-11 rounded-lg bg-neutral-900/70 border border-neutral-800 text-neutral-100 text-[14px] placeholder-neutral-600 focus:outline-none focus:border-[hsl(var(--kf-gold))]/70 transition"
                placeholder="Type it again"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((v) => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 h-8 w-8 rounded-md text-neutral-500 hover:text-neutral-300 flex items-center justify-center"
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </label>

          {/* Rule checklist */}
          <ul className="text-[11.5px] text-neutral-500 space-y-1 pt-1">
            <RuleRow ok={rules.len} label="At least 8 characters" />
            <RuleRow ok={rules.upper} label="One uppercase letter" />
            <RuleRow ok={rules.number} label="One number" />
            <RuleRow ok={rules.match} label="Both fields match" />
          </ul>

          {error && (
            <div
              data-testid="set-password-error"
              className="mt-2 text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-md px-3 py-2"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            data-testid="set-password-submit"
            disabled={!canSubmit}
            className="mt-4 w-full h-12 rounded-lg text-neutral-900 text-[13.5px] font-bold flex items-center justify-center gap-2 transition disabled:opacity-40 disabled:cursor-not-allowed"
            style={{
              background: `linear-gradient(180deg, ${GOLD} 0%, ${GOLD_DEEP} 100%)`,
              boxShadow: canSubmit ? `0 10px 30px -12px ${GOLD}66` : 'none',
            }}
          >
            <ShieldCheck className="h-4 w-4" />
            {loading ? 'Saving…' : 'Set password & continue'}
            {!loading && <ArrowRight className="h-4 w-4" />}
          </button>

          {/* Escape hatch — user got here by accident or wants to try another account */}
          <button
            type="button"
            onClick={bailOut}
            data-testid="set-password-signout"
            className="mt-2 w-full h-9 rounded-md text-[11.5px] uppercase tracking-[0.18em] font-semibold text-neutral-500 hover:text-neutral-300 transition"
          >
            Sign in with a different account
          </button>
        </form>
      </div>
    </div>
  );
}

function RuleRow({ ok, label }) {
  return (
    <li className="flex items-center gap-2">
      <span
        className={
          'inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[9px] font-bold ' +
          (ok
            ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
            : 'bg-neutral-800 text-neutral-600 border border-neutral-700')
        }
      >
        {ok ? '✓' : ''}
      </span>
      <span className={ok ? 'text-neutral-300' : 'text-neutral-500'}>{label}</span>
    </li>
  );
}
